import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { getSessionId } from "../utils/SessionId";
import { useCurrency } from "../context/CurrencyContext";
import CheckoutHeader from "../components/CheckoutHeader";
import CheckoutOrderSummary from "../components/CheckoutOrderSummary";
import Step1DeliveryAddress from "../components/checkout/Step1DeliveryAddress";
import Step2Shipping from "../components/checkout/Step2Shipping";
import Step3GiftWrap from "../components/checkout/Step3GiftWrap";
import Step4PaymentMethod from "../components/checkout/Step4PaymentMethod";
import {
  calculateTotals,
  validateStep1,
  getTitleText,
} from "../utils/checkoutHelpers";

// خريطة أرقام الاتصال بحسب الدولة المختارة من CurrencyContext
const COUNTRY_PHONE_CODE_MAP = {
  الكويت: "+965",
  الإمارات: "+971",
  السعودية: "+966",
  عمان: "+968",
  البحرين: "+973",
  قطر: "+974",
};

const Checkout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const sessionId = getSessionId();
  const { selectedCountry } = useCurrency();

  const cartItems = location.state?.cartItems || [];

  // State Management
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [emptyFields, setEmptyFields] = useState([]);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");

  const [formData, setFormData] = useState({
    shippingMethod: "standard",
    firstName: "",
    lastName: "",
    city: "",
    address: "",
    building: "",
    country: "الكويت",
    countryCode: "+965",
    phone: "",
    giftWrap: false,
    nationalId: "",
    title: "mr",
  });

  // مزامنة الدولة ورمز الهاتف مع الدولة المختارة من CurrencyPopup / CurrencyContext
  useEffect(() => {
    if (!selectedCountry) return;

    setFormData((prev) => ({
      ...prev,
      country: selectedCountry,
      countryCode:
        COUNTRY_PHONE_CODE_MAP[selectedCountry] || prev.countryCode || "+965",
    }));
  }, [selectedCountry]);

  // Calculate totals
  const { subtotal, taxAmount, finalTotal } = calculateTotals(cartItems);

  // Handlers
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // إزالة الحقل من قائمة الحقول الفارغة إذا تم ملؤه
    if (emptyFields.includes(name) && value.trim() !== "") {
      setEmptyFields((prev) => prev.filter((field) => field !== name));
    }
  };

  const saveAndContinue = (stepNumber) => {
    if (!completedSteps.includes(stepNumber)) {
      setCompletedSteps([...completedSteps, stepNumber]);
    }
  };

  const editStep = (stepNumber) => {
    setCurrentStep(stepNumber);
  };

  // Payment Functions - Ziina Integration
  const initializePayment = async (method) => {
    const paymentMethod = method || selectedPaymentMethod;

    if (!sessionId) {
      setError("خطأ في الجلسة");
      return;
    }

    if (!paymentMethod) {
      setError("يرجى اختيار طريقة الدفع");
      return;
    }

    setLoading(true);
    setError("");

    try {
      // الخطوة 1: حفظ الأوردر في الداتابيس
      const items = cartItems.map((item) => ({
        product_id: item.product_id,
        quantity: item.quantity,
        size: item.size || "",
        price:
          parseFloat(item.price_after) ||
          parseFloat(item.original_price) ||
          parseFloat(item.price_before) ||
          0,
      }));

      const totalPrice =
        typeof finalTotal === "number" ? finalTotal : parseFloat(finalTotal);

      if (isNaN(totalPrice) || totalPrice <= 0) {
        console.error("Invalid total price:", finalTotal, typeof finalTotal);
        throw new Error("السعر غير صحيح");
      }

      console.log("Creating order with price:", {
        total_price: totalPrice,
        total_price_type: typeof totalPrice,
        finalTotal_original: finalTotal,
        finalTotal_type: typeof finalTotal,
        subtotal,
        taxAmount,
      });

      const payload = {
        session_id: sessionId,
        first_name: formData.firstName,
        last_name: formData.lastName,
        country: formData.country,
        city: formData.city,
        address: formData.address,
        building: formData.building,
        phone: formData.phone,
        country_code: formData.countryCode,
        national_id: formData.nationalId,
        title: formData.title,
        // حفظ طريقة الدفع المختارة في الطلب (Card / ApplePay)
        payment_method: paymentMethod,
        total_price: totalPrice,
        items,
      };

      // استدعاء create_invoice لحفظ الأوردر
      const orderRes = await axios.post(
        "https://blomengdalis-tester.com/backend/create_invoice.php",
        payload,
        { headers: { "Content-Type": "application/json" } },
      );

      if (!orderRes.data?.order_id) {
        throw new Error("فشل إنشاء الطلب");
      }

      const order_id = orderRes.data.order_id;

      // الخطوة 2: إنشاء Payment Intent في Ziina
      const paymentRes = await axios.post(
        "https://blomengdalis-tester.com/backend/create-payment.php",
        { order_id },
        { headers: { "Content-Type": "application/json" } },
      );

      if (!paymentRes.data?.success || !paymentRes.data?.redirect_url) {
        throw new Error(paymentRes.data?.error || "فشل إنشاء عملية الدفع");
      }

      // الخطوة 3: التوجيه لصفحة الدفع في Ziina
      window.location.href = paymentRes.data.redirect_url;
    } catch (err) {
      let errorMessage = "حدث خطأ أثناء المعالجة";

      if (err.response) {
        const errorData = err.response.data;
        console.error("Payment API error response:", errorData);
        if (errorData?.message) {
          errorMessage = errorData.message;
        } else if (errorData?.error) {
          errorMessage = errorData.error;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      console.error("Payment initialization error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (method) => {
    const paymentMethod = method || selectedPaymentMethod;

    if (!paymentMethod) {
      setError("يرجى اختيار طريقة الدفع");
      return;
    }

    await initializePayment(paymentMethod);
  };

  // Step Navigation Handlers
  const handleStep1Next = () => {
    if (validateStep1(formData, setEmptyFields, setError)) {
      saveAndContinue(1);
      setCurrentStep(2);
    }
  };

  const handleStep2Next = () => {
    saveAndContinue(2);
    setCurrentStep(3);
  };

  const handleStep3Next = () => {
    saveAndContinue(3);
    setCurrentStep(4);
  };

  const handleStep4Next = (method) => {
    saveAndContinue(4);
    handleSubmit(method);
  };

  return (
    <div dir="rtl" className="min-h-screen bg-white">
      <div className="grid grid-cols-1 lg:grid-cols-3">
        {/* LEFT */}
        <div className="lg:col-span-2">
          <CheckoutHeader />

          <div className="px-4 py-6 max-w-2xl mx-auto">
            {error && (
              <div className="bg-red-100 text-red-700 p-3 rounded mb-4 text-sm">
                {error}
              </div>
            )}

            {/* STEP 1 - عنوان التسليم */}
            {currentStep === 1 && (
              <Step1DeliveryAddress
                formData={formData}
                handleChange={handleChange}
                emptyFields={emptyFields}
                setFormData={setFormData}
                currentStep={currentStep}
                onNext={handleStep1Next}
                validateStep1={() =>
                  validateStep1(formData, setEmptyFields, setError)
                }
              />
            )}

            {/* STEP 2 - خيارات التوصيل */}
            {currentStep === 2 && (
              <Step2Shipping
                formData={formData}
                getTitleText={getTitleText}
                completedSteps={completedSteps}
                currentStep={currentStep}
                onEdit={editStep}
                onNext={handleStep2Next}
              />
            )}

            {/* STEP 3 - التغليف والهدايا */}
            {currentStep === 3 && (
              <Step3GiftWrap
                formData={formData}
                getTitleText={getTitleText}
                completedSteps={completedSteps}
                currentStep={currentStep}
                onEdit={editStep}
                onNext={handleStep3Next}
              />
            )}

            {/* STEP 4 - طريقة الدفع */}
            {currentStep === 4 && (
              <Step4PaymentMethod
                formData={formData}
                getTitleText={getTitleText}
                completedSteps={completedSteps}
                currentStep={currentStep}
                onEdit={editStep}
                selectedPaymentMethod={selectedPaymentMethod}
                setSelectedPaymentMethod={setSelectedPaymentMethod}
                onNext={handleStep4Next}
                loading={loading}
                error={error}
              />
            )}
          </div>
        </div>

        {/* RIGHT */}
        <CheckoutOrderSummary
          cartItems={cartItems}
          totalAmount={subtotal}
          taxAmount={taxAmount}
          finalTotal={finalTotal}
        />
      </div>
    </div>
  );
};

export default Checkout;
